/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import * as util from "util";

import type { Context } from "@azure/functions-v3";
import { addErrorAttributes, setPropagatedResourceAttributes } from "@dynatrace/opentelemetry-core";
import { SpanKind, SpanStatusCode } from "@opentelemetry/api";

import { getStartAttributes, getExitAttributes } from "./AttributeDetector";
import { getTriggerType } from "./Bindings";
import { logger } from "./Logger";
import { detectResource } from "./ResourceDetector";
import { extractContext, getTracer, isAzureHttpRequestV3 } from "./Util";

// ============================================================================

let isV3Ctx: boolean | undefined;

// only exported for testing
export function _testClearIsV3Ctx(): void {
	isV3Ctx = undefined;
}

// ============================================================================
function isObject(obj: unknown): boolean {
	return obj != null && typeof(obj) === "object";
}

// ============================================================================
/**
 * Checks if the provided object is a V3 (programming model) Context
 * @param maybeContext context to be checked
 * @returns true if provided parameter is a V3 Context, otherwise false
 */
function isV3Context(maybeContext: unknown): maybeContext is Context {
	if (isV3Ctx == null) {
		isV3Ctx = isObject(maybeContext) &&
			(maybeContext as Context).invocationId != null &&
			isObject((maybeContext as Context).executionContext) &&
			isObject((maybeContext as Context).bindings) &&
			isObject((maybeContext as Context).bindingDefinitions);

		if (isV3Ctx === false) {
			logger.info("Only V3 Azure Functions programming model is supported. No data will be exported");
		}
	}

	return isV3Ctx;
}

// ============================================================================

// Define our own AzureFunction type to remove dependency on @azure/functions-v3
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AzureFunction = (context: any, ...args: unknown[]) => Promise<unknown> | void;

// ============================================================================
/**
 * Wraps an azure function handler for tracing.
 *
 * The returned function handler shall replace the exiting one. It starts a span on each invocation and calls the
 * original handler with the span set as active. The span is ended once the original function returns.
 *
 * Function arguments, this, return value and exceptions are forwarded by the wrapped handler to the original.
 *
 * @param handler the handler function to wrap
 * @returns the wrapped handler function
 */
export function wrapHandler(handler: AzureFunction): AzureFunction {
	const isAsyncFunction = util.types.isAsyncFunction(handler);
	logger.info(`wrapHandler: ${handler.name}, isAsyncFunction ${isAsyncFunction}`);

	// as of now only async functions are supported. Azure doc recommends to use async functions even if await is not used.
	// eslint-disable-next-line max-len
	// see https://docs.microsoft.com/en-us/azure/azure-functions/functions-reference-node?tabs=v2-v3-v4-export%2Cv2-v3-v4-done%2Cv2%2Cv2-log-custom-telemetry%2Cv2-accessing-request-and-response%2Cwindows-setting-the-node-version#use-async-and-await
	if (!isAsyncFunction) {
		logger.info("Only async handler functions are supported. No data will be exported");
		return handler;
	}

	return async function dtHandler(this: never, context: unknown, ...args: unknown[]): Promise<unknown> {
		if (!isV3Context(context)) {
			// If the first argument is not a V3 Context, then it's not a V3 programming model.
			// V4 programming model has Request as a handler's first argument
			return Reflect.apply(handler, this, [context, ...args]) as unknown;
		}

		const req = args[0];
		const azureHttpRequest = isAzureHttpRequestV3(req) ? req : undefined;
		const triggerType = getTriggerType(context);
		const resourceAttributes = detectResource(context?.executionContext?.functionName);
		const attributes = {
			...resourceAttributes,
			...getStartAttributes(triggerType, azureHttpRequest)
		};
		const name = context?.executionContext?.functionName ?? "invoke";
		const ctx = extractContext(triggerType, azureHttpRequest);
		return getTracer().startActiveSpan(name, { kind: SpanKind.SERVER, attributes }, ctx, async (span) => {
			setPropagatedResourceAttributes(span, resourceAttributes);
			try {
				// call the original handler and await the result
				const result = await Reflect.apply(handler, this, [context, ...args]);

				const exitAttributes = getExitAttributes(triggerType, context, result);
				if (exitAttributes != null) {
					span.setAttributes(exitAttributes);
				}
				return result;
			} catch (e) {
				addErrorAttributes(span, e);
				span.setStatus({ code: SpanStatusCode.ERROR });
				throw e;
			} finally {
				span.end();
			}
		});
	};
}
